'use client';

import { useEffect, useRef, useState } from 'react';

type Category = { id: string; label: string };

/**
 * Sticky FAQ category nav. Labels are passed in from the server parent (locale-
 * aware) – the component stays "dumb": no locale logic of its own. The
 * `ariaLabel` is the localized nav landmark name.
 */
export function CategoryNav({
  categories,
  ariaLabel,
}: {
  categories: Category[];
  ariaLabel: string;
}) {
  const [active, setActive] = useState<string | null>(null);
  const linkRefs = useRef<Record<string, HTMLAnchorElement | null>>({});

  // Section ids are stable across renders; depend on them (not the array
  // reference, which the server parent recreates each render).
  const sectionIds = categories.map((c) => c.id).join(',');

  useEffect(() => {
    const ids = sectionIds.split(',');
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((s): s is HTMLElement => Boolean(s));

    if (!('IntersectionObserver' in window) || sections.length === 0) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const id = visible[0].target.id;
          setActive(id);

          // Aktiven Chip NUR horizontal in die Nav-Leiste scrollen. KEIN
          // scrollIntoView: das scrollt auch die nächste VERTIKALE Scroll-Achse
          // (das Fenster) und kämpft während des Scroll-Spy bei jedem
          // Intersection-Wechsel gegen den User-Scroll – auf iOS-Momentum führt
          // das dazu, dass sich die Seite nicht mehr scrollen lässt. scrollBy am
          // horizontalen Container bewegt ausschließlich die Chip-Leiste.
          const link = linkRefs.current[id];
          const container = link?.parentElement;

          if (link && container) {
            const linkRect = link.getBoundingClientRect();
            const contRect = container.getBoundingClientRect();
            const delta =
              linkRect.left -
              contRect.left -
              (contRect.width - linkRect.width) / 2;

            container.scrollBy({ left: delta, behavior: 'smooth' });
          }
        }
      },
      {
        rootMargin: '-130px 0px -55% 0px',
        threshold: [0, 0.1, 0.5],
      },
    );

    sections.forEach((s) => observer.observe(s));

    return () => observer.disconnect();
  }, [sectionIds]);

  return (
    <nav className="cat-nav" aria-label={ariaLabel}>
      <div className="cat-nav-inner container">
        {categories.map((c) => (
          <a
            key={c.id}
            href={`#${c.id}`}
            ref={(el) => {
              linkRefs.current[c.id] = el;
            }}
            className={active === c.id ? 'is-active' : undefined}
          >
            {c.label}
          </a>
        ))}
      </div>
    </nav>
  );
}
